import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Play, RefreshCw, CheckCircle2, AlertCircle, Clock, Sparkles, Zap, PackagePlus } from "lucide-react";

const BRANDS = ["ASOS", "Mango", "Meski", "New Look", "Next", "Zara", "River Island", "Topshop"];

const StatusIcon = ({ status }) => {
    if (status === "ok") return <CheckCircle2 className="h-4 w-4 text-green-700" strokeWidth={1.5} />;
    if (status === "empty") return <AlertCircle className="h-4 w-4 text-yellow-600" strokeWidth={1.5} />;
    return <AlertCircle className="h-4 w-4 text-red-700" strokeWidth={1.5} />;
};

export default function Crawlers() {
    const [busy, setBusy] = useState(false);
    const [brandBusy, setBrandBusy] = useState(null);
    const [preview, setPreview] = useState(null); // last run results (not committed)
    const [history, setHistory] = useState([]);
    const [committing, setCommitting] = useState(false);

    const loadHistory = async () => {
        try {
            const r = await api.get("/crawlers/history");
            setHistory(r.data);
        } catch {}
    };

    useEffect(() => { loadHistory(); }, []);

    const run = async ({ brand = null, commit = false } = {}) => {
        setBusy(true);
        if (brand) setBrandBusy(brand);
        try {
            const r = await api.post("/crawlers/run", { brand, commit, limit_per_brand: 30 });
            if (commit) {
                toast.success(`${r.data.added} new products imported into catalog`);
                setPreview(null);
            } else {
                setPreview(r.data);
                const total = r.data.results.reduce((s, x) => s + (x.products?.length || 0), 0);
                toast.success(`Scraped ${total} items · review below then commit`);
            }
            loadHistory();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Crawler failed");
        } finally {
            setBusy(false);
            setBrandBusy(null);
        }
    };

    const commit = async () => {
        setCommitting(true);
        try {
            const r = await api.post("/crawlers/run", { commit: true, limit_per_brand: 30 });
            toast.success(`Imported ${r.data.added} new products`);
            setPreview(null);
            loadHistory();
        } catch (e) {
            toast.error("Commit failed");
        } finally {
            setCommitting(false);
        }
    };

    // Per-brand notes (rendered via proxy so success rate is high)
    const BRAND_NOTES = {
        "ASOS":         "Cloudflare-protected. Bypassed via proxy.",
        "Mango":        "JS SPA. Rendered by proxy.",
        "Meski":        "Shopify JS. Rendered by proxy.",
        "New Look":     "Bot-protected. Bypassed via proxy.",
        "Next":         "Bot-protected. Bypassed via proxy.",
        "Zara":         "React SPA. Rendered by proxy.",
        "River Island": "Cloudflare + JS. Bypassed via proxy.",
        "Topshop":      "Served via ASOS. Bypassed via proxy.",
    };

    const totalFound = preview?.results?.reduce((s, r) => s + (r.products?.length || 0), 0) || 0;

    return (
        <div className="p-4 sm:p-6 lg:p-10 max-w-7xl">
            <div className="flex items-end justify-between mb-8 gap-6 flex-wrap">
                <div>
                    <div className="overline text-neutral-500">Auto Import</div>
                    <h1 className="font-display text-5xl tracking-tight leading-none mt-2">Brand Crawlers</h1>
                    <p className="mt-3 text-sm font-body text-neutral-500 max-w-2xl leading-relaxed">
                        Runs automatically every 24 hours across all 8 brands, extracting products from their &quot;New In&quot; women&apos;s pages via JSON-LD, embedded state and HTML fallbacks. Deduplicated by source URL &amp; title before writing to your catalog.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={() => run({ commit: false })}
                        disabled={busy}
                        variant="outline"
                        className="rounded-none h-11 border-black uppercase tracking-widest text-xs"
                        data-testid="run-all-preview-btn"
                    >
                        {busy && !brandBusy ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" strokeWidth={1.5}/> : <Sparkles className="h-4 w-4 mr-2" strokeWidth={1.5}/>}
                        Preview All
                    </Button>
                    <Button
                        onClick={() => run({ commit: true })}
                        disabled={busy}
                        className="rounded-none h-11 bg-black text-white hover:bg-neutral-800 uppercase tracking-widest text-xs"
                        data-testid="run-all-commit-btn"
                    >
                        <Zap className="h-4 w-4 mr-2" strokeWidth={1.5}/> Run &amp; Import Now
                    </Button>
                </div>
            </div>

            {/* Live-status banner */}
            <div className="border border-green-500 bg-green-50 p-5 mb-8">
                <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-700 shrink-0 mt-0.5" strokeWidth={1.5}/>
                    <div className="flex-1">
                        <div className="overline text-green-800 mb-1">ScraperAPI Proxy · Active with JS Rendering</div>
                        <p className="text-sm font-body text-neutral-700 leading-relaxed">
                            All fetches route through <b>ScraperAPI</b> with JS rendering and per-brand proxy tier (basic / premium / ultra&#8209;premium).
                            Best real-world yield today: <b>Zara</b> (structured data, most reliable), <b>ASOS/Topshop</b> (via premium proxy), <b>Meski</b> (Shopify).
                            <b>Next, New Look</b> and <b>Mango</b> maintain aggressive bot-detection that even ultra-premium proxy cannot always bypass — for those, the manual <b>Add Product</b> URL-paste is your reliable fallback.
                            Watch your ScraperAPI credit balance — JS rendering + premium is roughly 10-25 credits per fetch.
                        </p>
                    </div>
                </div>
            </div>

            {/* Brand grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
                {BRANDS.map((b) => (
                    <button
                        key={b}
                        onClick={() => run({ brand: b, commit: false })}
                        disabled={busy}
                        className="border border-border p-4 text-left hover:border-black transition-colors disabled:opacity-50"
                        data-testid={`brand-run-${b.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                        <div className="overline text-neutral-500">Brand</div>
                        <div className="font-display text-2xl tracking-tight mt-1 leading-none">{b}</div>
                        <div className="text-[10px] font-body text-neutral-500 mt-2 line-clamp-2 min-h-[2.5em]">{BRAND_NOTES[b]}</div>
                        <div className="mt-3 flex items-center gap-2 text-xs uppercase tracking-widest">
                            {brandBusy === b ? (
                                <><RefreshCw className="h-3 w-3 animate-spin" strokeWidth={1.5}/> Running…</>
                            ) : (
                                <><Play className="h-3 w-3" strokeWidth={1.5}/> Preview</>
                            )}
                        </div>
                    </button>
                ))}
            </div>

            {/* Preview */}
            {preview && (
                <section className="border border-black mb-10">
                    <header className="px-6 py-4 border-b border-border bg-yellow-50 flex flex-wrap items-center justify-between gap-4">
                        <div>
                            <div className="overline text-neutral-700">Preview</div>
                            <div className="font-display text-2xl mt-1">Found {totalFound} products across {preview.results.length} brands</div>
                        </div>
                        <Button
                            onClick={commit}
                            disabled={committing || totalFound === 0}
                            className="rounded-none h-11 bg-black text-white hover:bg-neutral-800 uppercase tracking-widest text-xs"
                            data-testid="commit-preview-btn"
                        >
                            <PackagePlus className="h-4 w-4 mr-2" strokeWidth={1.5}/>
                            {committing ? "Importing…" : `Import All ${totalFound}`}
                        </Button>
                    </header>
                    <div className="divide-y divide-border">
                        {preview.results.map((r) => (
                            <details key={r.brand} className="group">
                                <summary className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-neutral-50 select-none">
                                    <div className="flex items-center gap-3">
                                        <StatusIcon status={r.status} />
                                        <div className="font-display text-lg">{r.brand}</div>
                                        <div className="overline text-neutral-500">{r.status === "ok" ? `${r.products.length} items` : r.status.toUpperCase()}</div>
                                    </div>
                                    <div className="text-xs font-mono text-neutral-500">{r.message}</div>
                                </summary>
                                {r.products.length > 0 && (
                                    <div className="p-6 bg-neutral-50 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                                        {r.products.slice(0, 18).map((p, i) => (
                                            <div key={i} className="bg-white border border-border" data-testid={`preview-item-${r.brand}-${i}`}>
                                                <div className="aspect-[3/4] overflow-hidden bg-neutral-100">
                                                    {p.image_url ? (
                                                        <img src={p.image_url} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.style.display = 'none'; }}/>
                                                    ) : null}
                                                </div>
                                                <div className="p-2">
                                                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 truncate">{p.brand}</div>
                                                    <div className="font-body text-xs mt-1 line-clamp-2 min-h-[2em]">{p.title}</div>
                                                    <div className="font-mono text-xs mt-1 tabular-nums">{p.price ? `£${Number(p.price).toFixed(2)}` : "—"}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </details>
                        ))}
                    </div>
                </section>
            )}

            {/* History */}
            <section className="border border-border">
                <header className="px-6 py-4 border-b border-border flex items-center justify-between">
                    <div className="overline text-neutral-600 flex items-center gap-2">
                        <Clock className="h-3 w-3" strokeWidth={1.5}/> Run History
                    </div>
                    <div className="text-xs text-neutral-500 font-body">Auto-runs every 24h</div>
                </header>
                <div className="divide-y divide-border">
                    {history.length === 0 && <div className="p-8 text-center text-sm text-neutral-500 font-body">No runs yet — hit &quot;Run &amp; Import Now&quot; above.</div>}
                    {history.map((h) => {
                        const ok = h.brands.filter(b => b.status === "ok").length;
                        return (
                            <div key={h.id} className="p-5" data-testid={`history-${h.id}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <div className="font-mono text-xs text-neutral-500">{new Date(h.run_at).toLocaleString()}</div>
                                        <div className="font-body text-sm mt-1">
                                            <Badge variant="outline" className="rounded-none uppercase tracking-widest text-[9px] mr-2 border-black">{h.triggered_by}</Badge>
                                            {ok}/{h.brands.length} brands successful · <b>{h.added}</b> products imported
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {h.brands.map((b) => (
                                        <div key={b.brand} className="border border-border p-2 flex items-center gap-2 text-xs">
                                            <StatusIcon status={b.status} />
                                            <div className="flex-1 truncate">
                                                <div className="font-body truncate">{b.brand}</div>
                                                <div className="text-neutral-500 text-[10px] truncate">{b.found} found · {b.strategy || b.status}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
